import {
  AgentAchievement,
  AgentDetails,
  AgentPropertyTypes,
  AgentStatistics,
  AgentStatisticType,
  BestStatistics,
  Sales,
} from '@rma/public/compare-agent/private-api/da-compare-agent';

type LanguageSource = Pick<AgentDetails, 'hasProfileCateredLanguages' | 'cateredLanguages'>;
type SentimentSource = Pick<AgentDetails, 'hasProfileReviewAttributes' | 'reviewAttributes'>;
type AchievementSource = Pick<AgentDetails, 'hasRecognitionBadges' | 'isHighlyRecommended' | 'isTrustedAgent' | 'isPriceExpert'>;

export const getLanguages = ({ hasProfileCateredLanguages, cateredLanguages }: LanguageSource): string[] =>
  hasProfileCateredLanguages ? (cateredLanguages ?? []).map((language) => language?.key).filter((key): key is string => !!key) : [];

export const getSentiments = ({ hasProfileReviewAttributes, reviewAttributes }: SentimentSource): string[] =>
  hasProfileReviewAttributes ? (reviewAttributes ?? []).map((attribute) => attribute?.name).filter((name): name is string => !!name) : [];

export const getAchievements = ({
  hasRecognitionBadges,
  isHighlyRecommended,
  isTrustedAgent,
  isPriceExpert,
}: AchievementSource): AgentAchievement[] => {
  if (!hasRecognitionBadges) {
    return [];
  }

  const achievements: AgentAchievement[] = [];

  if (isHighlyRecommended) {
    achievements.push('top20');
  }
  if (isTrustedAgent) {
    achievements.push('trusted');
  }
  if (isPriceExpert) {
    achievements.push('priceExpert');
  }

  return achievements;
};

export const extractUniquePropertyTypes = (stats: AgentDetails[]): string[] => {
  const propertyTypeSet = new Set<string>();

  for (const { statistics } of stats) {
    const propertyTypes = statistics?.totaL_SALES_QTY?.byPropertyType ?? statistics?.activE_CAMPAIGNS?.byPropertyType;

    if (!propertyTypes) {
      continue;
    }

    for (const [key, value] of Object.entries(propertyTypes)) {
      if (value > 0) {
        propertyTypeSet.add(key);
      }
    }
  }

  return Array.from(propertyTypeSet);
};

export const getAgentPropertyTypes = (
  statistics: Record<AgentStatisticType, AgentStatistics>,
  propertyTypes: string[],
): AgentPropertyTypes[] => {
  return propertyTypes.map((propertyType) => ({
    propertyType,
    quantity: statistics.totaL_SALES_QTY.byPropertyType?.[propertyType] ?? 0,
    avg: statistics.avG_SALE_PRICE.byPropertyType?.[propertyType] ?? 0,
    total: statistics.totaL_SALES_VALUE.byPropertyType?.[propertyType] ?? 0,
  }));
};

export const hasBestStats = (stats: BestStatistics[], stat: BestStatistics) => {
  return stats.indexOf(stat) >= 0;
};

export const getSoldStats = (statistics: Record<AgentStatisticType, AgentStatistics>, bestStatistics: BestStatistics[]): Sales => {
  const stats = statistics['totaL_SALES_QTY'];

  return {
    total: stats?.totalValue ?? 0,
    lead: stats?.listingAgentValue ?? 0,
    support: (stats?.totalValue ?? 0) - (stats?.listingAgentValue ?? 0),
    bestPropertiesSoldTotal: hasBestStats(bestStatistics, 'PropertiesSoldTotal'),
    bestPropertiesSoldAsLead: hasBestStats(bestStatistics, 'PropertiesSoldAsLead'),
    bestPropertiesSoldAsSupport: hasBestStats(bestStatistics, 'PropertiesSoldAsSupport'),
  };
};
